import { ApplicationFormPageEnum } from '../types';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { getCurrentRoute } from '@utils/location';
import { AddStudentLoan } from '@pages/ApplicationFormPage/AddStudentLoan/AddStudentLoan';
import { loanQuestions } from '../AddStudentLoan/questions';
import { parseApplicationInProgress } from '../helpers';
import { StudentLoans } from '../StudentLoans/StudentLoans';
import { IApplicationInProgressQuestion } from '../types';
import { StudentLoanWrapperType, IStudentLoan } from './types';

export const StudentLoansWrapper = () => {
  const intl = useIntl();
  const [studentLoans, setStudentLoans] = useState<IStudentLoan[]>([]);
  const [currentPage, setCurrentPage] = useState<StudentLoanWrapperType>(ApplicationFormPageEnum.STUDENT_LOANS);
  const [studentLoanToEdit, setStudentLoanToEdit] = useState<IStudentLoan | null>(null);
  const isEdit = !!studentLoanToEdit;
  const { id = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { data: applicationInProgress, refetch, isLoading } = useApplicationInProgressById(id);
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress, {
    onSuccess: refetch,
  });

  const studentLoanFormatter = (studentLoan: IStudentLoan): IApplicationInProgressQuestion[] => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...studentLoanDetails } = studentLoan;

    return Object.entries(studentLoanDetails).map(([key, value]) => {
      return {
        question: intl.formatMessage({
          id: loanQuestions[key].labelKey,
        }),
        key,
        answer: value,
      };
    });
  };

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const formattedResponse = applicationInProgress[currentRoute].map((studentLoan: any) => ({
        id: uuidv4(),
        ...parseApplicationInProgress(studentLoan),
      })) as IStudentLoan[];

      setStudentLoans(formattedResponse);
    }
  }, [applicationInProgress]);

  const patchStudentLoans = (studentLoansList: IStudentLoan[]) => {
    const questions = studentLoansList.map(studentLoanFormatter);
    patchApplicationInProgress({ questions, currentRoute, applicationId: id });
  };

  const editStudentLoanRedirectHandler = (loan: IStudentLoan) => {
    setStudentLoanToEdit(loan);
    setCurrentPage(ApplicationFormPageEnum.ADD_STUDENT_LOAN);
  };

  const addStudentLoanHandler = (loan: IStudentLoan) => {
    patchStudentLoans([...studentLoans, loan]);
    setCurrentPage(ApplicationFormPageEnum.STUDENT_LOANS);
  };

  const goBackHandler = () => {
    setCurrentPage(ApplicationFormPageEnum.STUDENT_LOANS);
    setStudentLoanToEdit(null);
  };

  const editStudentLoanHandler = (loan: IStudentLoan) => {
    const newStudentLoans = studentLoans.map(loanUnit => {
      return loanUnit.id === loan.id ? loan : loanUnit;
    });

    patchStudentLoans(newStudentLoans);
    goBackHandler();
  };

  const removeStudentLoanByIdHandler = (id: string) => {
    const newStudentLoans = studentLoans.filter(loan => loan.id !== id);
    patchStudentLoans(newStudentLoans);
  };

  const submitStudentLoanHandler = (loan: IStudentLoan) =>
    isEdit ? editStudentLoanHandler(loan) : addStudentLoanHandler(loan);

  const ActiveFormPage = () => {
    switch (currentPage) {
      case ApplicationFormPageEnum.STUDENT_LOANS:
        return (
          <StudentLoans
            studentLoans={studentLoans}
            onAddStudentLoanRedirect={() => setCurrentPage(ApplicationFormPageEnum.ADD_STUDENT_LOAN)}
            onEditStudentLoanRedirect={editStudentLoanRedirectHandler}
            onRemoveStudentLoanById={removeStudentLoanByIdHandler}
          />
        );
      case ApplicationFormPageEnum.ADD_STUDENT_LOAN:
        return (
          <AddStudentLoan
            studentLoanToEdit={studentLoanToEdit}
            onSubmitStudentLoan={submitStudentLoanHandler}
            onGoBack={goBackHandler}
          />
        );
      default:
        return null;
    }
  };

  return isLoading ? null : <ActiveFormPage />;
};
